import { Component, PLATFORM_ID, ViewChild, inject } from '@angular/core';
import { GruposApiService, ListRetrieveGrupo } from '@cei-ufg/ngx-cei-seguranca';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import {
  CeiAcaoLinhaTabela,
  CeiButtonComponent,
  CeiConfirmDialogService,
  CeiScrollableActionTableComponent,
  CeiScrollableActionTablePageFetcher,
  CeiSnackBarService,
  CeiTableColumn,
  CeiTablePage,
} from '@cei-ufg/ngx-arquitetura-cei-frontend';
import { notifyAdminFormSuccess } from '../../shared/admin-form.utils';
import { notifyAdminListError } from '../../shared/admin-list.utils';
import {
  GerenciadorLarguraColunaId,
  atualizarColunasIdAoCarregarPagina,
} from '../../shared/admin-list-columns';
import { ADMIN_CARREGAMENTO_TABELA } from '../../shared/admin-loading.config';
import { normalizarPaginaApi, paraRequisicaoPaginacaoOpenApi } from '../../shared/admin-paginacao.utils';
import { firstValueFrom, map } from 'rxjs';

/** Listagem de grupos com paginação por scroll e ações declarativas CEI. */
@Component({
  selector: 'cei-admin-grupo-list',
  standalone: true,
  imports: [CommonModule, RouterModule, CeiButtonComponent, CeiScrollableActionTableComponent],
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
})
export class AdminGrupoListComponent {
  @ViewChild('tabela') private tabela?: CeiScrollableActionTableComponent<ListRetrieveGrupo>;

  private readonly colunasBase: CeiTableColumn<ListRetrieveGrupo>[] = [
    { key: 'id', label: 'ID', property: 'id', sortable: true },
    { key: 'name', label: 'Nome', property: 'name', sortable: true },
  ];

  private readonly larguraColunaId = new GerenciadorLarguraColunaId();

  protected columns = this.larguraColunaId.aplicar(this.colunasBase);

  protected readonly carregamentoTabela = ADMIN_CARREGAMENTO_TABELA;

  protected readonly autoLoad = isPlatformBrowser(inject(PLATFORM_ID));

  protected readonly acoesLinha: CeiAcaoLinhaTabela<ListRetrieveGrupo>[] = [
    {
      id: 'editar',
      rotulo: 'Editar',
      icone: 'editar',
      rota: (linha) => ['/admin/grupos/atualizar', linha.id],
    },
    {
      id: 'excluir',
      rotulo: 'Excluir',
      icone: 'excluir',
      executar: (linha) => this.excluirGrupo(linha),
    },
  ];

  protected readonly buscarPagina: CeiScrollableActionTablePageFetcher<ListRetrieveGrupo> = (request) =>
    this.gruposApi
      .grupoList(paraRequisicaoPaginacaoOpenApi(request), 'body', false, { transferCache: false })
      .pipe(map((resposta) => normalizarPaginaApi(resposta)));

  private readonly gruposApi = inject(GruposApiService);
  private readonly confirmDialog = inject(CeiConfirmDialogService);
  private readonly snackBar = inject(CeiSnackBarService);
  private readonly router = inject(Router);

  /** Navega para detalhes ao selecionar a linha na pílula. */
  protected aoSelecionarLinha(grupo: ListRetrieveGrupo): void {
    void this.router.navigate(['/admin/grupos/detalhe', grupo.id]);
  }

  /** Exibe erro de carregamento remoto emitido pela tabela. */
  protected aoErroCarregamento(erro: unknown): void {
    notifyAdminListError(this.snackBar, 'Erro ao carregar grupos.', erro);
  }

  protected aoCarregarPagina(pagina: CeiTablePage<ListRetrieveGrupo>): void {
    const colunas = atualizarColunasIdAoCarregarPagina(this.larguraColunaId, this.colunasBase, pagina);
    if (colunas) {
      this.columns = colunas;
    }
  }

  /** Confirma e remove grupo; recarrega a listagem após sucesso. */
  private excluirGrupo(grupo: ListRetrieveGrupo): void {
    this.confirmDialog
      .confirm({
        title: 'Excluir grupo',
        content: `Deseja realmente excluir o grupo "${grupo.name}"?`,
        yesLabel: 'Sim',
        noLabel: 'Não',
      })
      .subscribe(async (confirmed) => {
        if (!confirmed) {
          return;
        }

        try {
          await firstValueFrom(this.gruposApi.grupoDestroy({ id: grupo.id }));
          notifyAdminFormSuccess(this.snackBar, 'Grupo excluído com sucesso!');
          this.tabela?.recarregar();
        } catch (err) {
          notifyAdminListError(this.snackBar, 'Erro ao excluir grupo.', err);
        }
      });
  }
}
